import { Inject, Injectable, OnDestroy } from '@angular/core'
import { Router } from '@angular/router'
import { AuthContextStorageService } from './auth-context-storage.service'
import { UserContextStorageService } from './user-context/user-context-storage.service'
import { AuthBackendService } from './auth-backend.service'
import { GoogleAnalyticsService } from '../monitoring/google-analytics.service'
import { DialogService } from '../../core/services/dialog.service'
import { CaStateService } from '../../core/services/ca-state.service'
import { IntercomService } from '../monitoring/intercom.service'
import { CookieService } from '../../shared/services/cookie.service'
import { SentryService } from '../monitoring/sentry.service'
import { Store } from '@ngrx/store'
import { AuthActions } from './state/action-types'
import { selectAccessToken } from './state/auth.selectors'
import { Subscription } from 'rxjs'
import { finalize } from 'rxjs/operators'
import { IAuthAccessToken } from './state/auth.reducers'
import { UserContextActions } from './user-context/state/action-types'
import { ContractTemplateDatastore } from '../../features/contracts/services/contract-template-datastore.service'
import { ConcernedDatastoreService } from '../../domain/subject/services/concerned-datastore.service'
import { isInvalidTokenError } from './invalid-token'
import { MapColorGeneratorActions } from '../../features/map/common/map-color-generator/state/action-types'


@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {

    private _isLoggingOut = false
    private sessionExpiredDialogOpen = false
    private accessTokenExpirationIntervalId: number
    private accessToken: IAuthAccessToken
    private onStartupPromise: Promise<any> | null = null

    #selectAccessToken: Subscription

    constructor(@Inject(Store) private store: Store,
                @Inject(Router) private router: Router,
                @Inject(AuthBackendService) private authBackendService: AuthBackendService,
                @Inject(CaStateService) private caStateService: CaStateService,
                @Inject(CookieService) private cookieService: CookieService,
                @Inject(DialogService) private dialogService: DialogService,
                @Inject(GoogleAnalyticsService) private googleAnalyticsService: GoogleAnalyticsService,
                @Inject(IntercomService) private intercomService: IntercomService,
                @Inject(SentryService) private sentryService: SentryService,
                @Inject(AuthContextStorageService) private authContextStorageService: AuthContextStorageService,
                @Inject(UserContextStorageService) private userContextStorageService: UserContextStorageService,
                @Inject(ContractTemplateDatastore) private contractTemplateDatastore: ContractTemplateDatastore,
                @Inject(ConcernedDatastoreService) private concernedDatastoreService: ConcernedDatastoreService
                ) {

        this.#selectAccessToken = this.store.select(selectAccessToken).subscribe(data => {
            this.accessToken = data
        })
    }

    get isLoggingOut(): boolean {
        return this._isLoggingOut
    }

    ngOnDestroy() {
        this.#selectAccessToken?.unsubscribe()
    }

    changeDefaultProjectKey(projectKey): Promise<any> {
        return this.authBackendService.changeDefaultProjectKey(projectKey)
    }

    changePassword(oldPassword, newPassword): Promise<any> {
        return this.authBackendService.changePassword(this.userContextStorageService.getUser().username, oldPassword, newPassword)
    }

    onStartup(): Promise<any> {
        // Prevent multiple concurrent refresh attempts - reuse existing promise if one is in progress
        // This prevents race condition where multiple guards/resolvers call onStartup() simultaneously,
        // causing the first to succeed (and rotate the token) while subsequent ones fail with invalid token
        if (this.onStartupPromise) {
            return this.onStartupPromise
        }

        const projectKey = this.accessToken?.projectKey || null
        this.onStartupPromise = this.authBackendService
            .refreshAccessToken(projectKey)
            .then((loginResponse) => {
                this.onStartupPromise = null
                return this.setAuthContext(loginResponse.accessToken, loginResponse.user, loginResponse.projectKeys)
            })
            .catch((error) => {
                this.onStartupPromise = null
                throw error
            })

        return this.onStartupPromise
    }

    login(username: string, password, keep: boolean): Promise<{success: boolean; code?: number}> {
        // reset user context data (user, project etc.)
        return new Promise((resolve, reject) => {
            this.store.dispatch(UserContextActions.reset())
            this.authBackendService.login(username, password, keep).subscribe((loginResponse) => {
                this.googleAnalyticsService.logEvent('login', 'logged_in', keep ? 'permanent' : 'onetime')
                this.setAuthContext(loginResponse.accessToken, loginResponse.user, loginResponse.projectKeys).then(() => {
                    resolve({success: true})
                })
            }, (error) => {
                this.googleAnalyticsService.logEvent('login', 'login_failed')
                reject( {success: false, code: error.status})
            })
        })
    }

    logout(): void {
        this.googleAnalyticsService.logEvent('login', 'logged_out')
        this._isLoggingOut = true

        // Navigate first so CanDeactivate guards can show "unsaved changes" modal.
        // Only if navigation succeeds (user confirmed or no guard), perform actual logout.
        this.router.navigate(['/login']).then((navigated) => {
            if (navigated) {
                this.authBackendService.logout().subscribe(
                    () => this.resetAuthContext(),
                    (error) => {
                        console.error('An error occurred:', error)
                        this.resetAuthContext()
                    }
                )
            }
            this._isLoggingOut = false
        })
    }

    resetAuthContext(): void {
        if (this.accessTokenExpirationIntervalId != null) {
            clearInterval(this.accessTokenExpirationIntervalId)
            this.accessTokenExpirationIntervalId = null
        }

        // reset access token data
        this.store.dispatch(AuthActions.reset())
        // reset user context data (user, project etc.)
        this.store.dispatch(UserContextActions.reset())

        this.intercomService.shutdown()
        this.sentryService.logoutUser()
        this.googleAnalyticsService.userLoggedOut()
    }

    authorizationTokenExpired(): void {
        this.caStateService.setLoginState()

        // Dávka souběžných 401 nesmí otevřít víc oken; další volání jen přesměruje na login.
        if (this.sessionExpiredDialogOpen) {
            return
        }

        // Okna otevřená před odhlášením (formulář, potvrzení odchodu) by jinak zůstala viset nad loginem
        this.dialogService.closeAll()

        this.sessionExpiredDialogOpen = true
        this.dialogService.showWarningDialog('Platnost přihlášení vypršela. Přihlaste se prosím znovu.')
            .pipe(finalize(() => this.sessionExpiredDialogOpen = false))
            .subscribe(() => this.caStateService.setLoginState())
    }

    switchProject(projectKey): Promise<any> {
        return this.authBackendService.refreshAccessToken(projectKey).then((loginResponse) => {
            return this.setAuthContext(loginResponse.accessToken, loginResponse.user, loginResponse.projectKeys)
        }).then(() => {
            return this.changeDefaultProjectKey(this.authContextStorageService.getProjectKey())
        })
    }

    /*************************************************************************/

    private refreshAccessToken = (): Promise<any> => {
        if (this.onStartupPromise) {
            return this.onStartupPromise
        }

        const projectKey = this.accessToken?.projectKey || null
        this.onStartupPromise = this.authBackendService
            .refreshAccessToken(projectKey)
            .then((loginResponse) => {
                this.onStartupPromise = null
                return this.setAuthContextAfterRefreshAccessToken(loginResponse.accessToken, loginResponse.user, loginResponse.projectKeys)
            })
            .catch((error) => {
                this.onStartupPromise = null
                throw error
            })

        return this.onStartupPromise
    }

    private setAuthContextAfterRefreshAccessToken(accessToken, user, projectKeys): Promise<any> {
        if (this.accessTokenExpirationIntervalId != null) {
            clearInterval(this.accessTokenExpirationIntervalId)
        }

        this.accessTokenExpirationIntervalId = setInterval(() => {
            this.refreshAccessToken().catch((error) => this.logoutIfTokenInvalid('refresh_interval', error))
        }, 870000) as unknown as number

        this.store.dispatch(AuthActions.authSetAccessToken({
            accessToken: accessToken
        }))
        this.store.dispatch(UserContextActions.userContextSetUser({
            user: user
        }))
        this.store.dispatch(UserContextActions.setProjectKeys({
            projectKeys: projectKeys
        }))

        return this.authBackendService
            .getProjectByAuthorizationToken()
            .then((project) => this.store.dispatch(UserContextActions.setProjectAfterRefreshAccessToken({
                project: project
            })))
            .catch((error) => this.handleProjectLoadError(error))
    }

    private setAuthContext(accessToken, user, projectKeys): Promise<void> {
        if (this.accessTokenExpirationIntervalId != null) {
            clearInterval(this.accessTokenExpirationIntervalId)
        }
        // Reset datastores to prevent new project or new login has another project data
        this.contractTemplateDatastore.resetLoadedTemplates()
        this.concernedDatastoreService.reset()
        this.store.dispatch(MapColorGeneratorActions.resetColors())

        this.accessTokenExpirationIntervalId = setInterval(() => {
            this.refreshAccessToken().catch((error) => this.logoutIfTokenInvalid('refresh_interval', error))
        }, 870000) as unknown as number

        this.store.dispatch(AuthActions.authSetAccessToken({
            accessToken: accessToken
        }))
        this.store.dispatch(UserContextActions.userContextSetUser({
            user: user
        }))
        this.store.dispatch(UserContextActions.setProjectKeys({
            projectKeys: projectKeys
        }))

        this.cookieService.setLoggedInCookie()
        this.intercomService.loginUser(
            this.userContextStorageService.getUser().username,
            this.userContextStorageService.getUser().name)
        this.sentryService.loginUser(
            this.userContextStorageService.getUser().username,
            this.userContextStorageService.getUser().name)
        this.googleAnalyticsService.userLoggedIn(
            this.userContextStorageService.getUser().username,
            this.accessToken.projectKey,
            this.userContextStorageService.getUser().webRegistration)

        return this.authBackendService
            .getProjectByAuthorizationToken()
            .then((project) => {
                this.store.dispatch(UserContextActions.userContextSetProject({
                    project: project
                }))

                this.store.dispatch(UserContextActions.setProject({
                    project: project
                }))
            })
            .catch((error) => this.handleProjectLoadError(error))
    }

    private handleProjectLoadError(error): void {
        console.warn('getProjectByAuthorizationToken failed:', error?.status)

        this.logoutIfTokenInvalid('project_load', error)
    }

    /**
     * Jediné místo, které rozhoduje o odhlášení kvůli chybové odpovědi, a jediné, které to měří.
     *
     * Měření je tu proto, že tahle cesta je v produkci dnes **neviditelná** (DEV-2270): chyba je
     * ošetřená `.catch()` na místě volání, takže se do Sentry nedostane, a uživatel uvidí jen
     * hlášku o nečinnosti, nerozeznatelnou od normálního vypršení session. Bez těch událostí
     * nelze říct, jak často se to reálným uživatelům děje.
     */
    private logoutIfTokenInvalid(source: string, error): void {
        const status = error?.status ?? 'none'

        if (isInvalidTokenError(error)) {
            this.googleAnalyticsService.logEvent('login', 'token_invalid_logout', `${source}:${status}`)
            this.resetAuthContext()
            this.authorizationTokenExpired()

            return
        }

        this.googleAnalyticsService.logEvent('login', 'token_error_ignored', `${source}:${status}`)
    }
}
